import { CaretLeft, Link, SquaresFour } from '@phosphor-icons/react';
import { Avatar, Badge, Button, cn, IconButton, Stack } from '@reelplicate/ui';

import type { Account, AccountGroup } from '@/features/calendar/calendar-model';
import { PlatformDot } from '@/features/calendar/platform-stack';
import { PLATFORM_NAMES } from '@/features/calendar/platforms';

/** `'all'` or an account id. */
export type Channel = string;
export const ALL_CHANNELS = 'all';

type ChannelsPanelProps = {
  groups: AccountGroup[];
  accounts: Account[];
  /** Ids of the accounts with numbers. */
  tracked: Set<string>;
  selected: Channel;
  onSelect: (channel: Channel) => void;
  onCollapse: () => void;
  onConnectAccount: () => void;
};

const item =
  'flex w-full items-center rounded-md text-left outline-none focus-visible:shadow-focus-ring';

/**
 * Figma "Channels" (166:91): pick whose analytics to see - every account, or
 * one. Accounts that aren't tracked yet are dimmed and can't be picked.
 */
export function ChannelsPanel({
  groups,
  accounts,
  tracked: trackedIds,
  selected,
  onSelect,
  onCollapse,
  onConnectAccount,
}: ChannelsPanelProps) {
  return (
    <Stack
      as="nav"
      aria-label="Channels"
      gap={3}
      className="bg-bg-surface border-border-subtle h-full w-[280px] shrink-0 overflow-y-auto border-r px-4 py-6"
    >
      <div className="flex items-center gap-2">
        <h2 className="text-heading-h4 text-text-primary flex-1">Channels</h2>
        <IconButton
          icon={CaretLeft}
          size="sm"
          aria-label="Hide channels"
          onClick={onCollapse}
        />
      </div>

      <button
        type="button"
        aria-pressed={selected === ALL_CHANNELS}
        onClick={() => onSelect(ALL_CHANNELS)}
        className={cn(
          item,
          'gap-2.5 rounded-[10px] px-3 py-2.5',
          selected === ALL_CHANNELS ? 'bg-brand-subtle' : 'hover:bg-bg-subtle',
        )}
      >
        <SquaresFour aria-hidden className="text-icon-secondary size-[18px]" />
        <span className="text-label-md text-text-primary flex-1">All accounts</span>
        <span className="text-caption text-text-secondary">{accounts.length}</span>
      </button>

      {groups.map((group) => (
        <Stack key={group.id} gap={0.5}>
          <div className="flex items-center gap-1.5 py-1.5">
            <h3 className="text-label-md text-text-primary">{group.name}</h3>
            <Badge size="sm" tone={group.kind === 'brand' ? 'brand' : 'neutral'}>
              {group.kind === 'brand' ? 'Brand' : 'Persona'}
            </Badge>
          </div>
          <ul className="flex flex-col gap-0.5">
            {accounts
              .filter((a) => a.groupId === group.id)
              .map((account) => {
                const tracked = trackedIds.has(account.id);
                const active = selected === account.id;
                return (
                  <li key={account.id}>
                    <button
                      type="button"
                      aria-pressed={active}
                      disabled={!tracked}
                      onClick={() => onSelect(account.id)}
                      aria-label={`${account.handle} · ${PLATFORM_NAMES[account.platform]}${tracked ? '' : ' · not tracked'}`}
                      className={cn(
                        item,
                        'gap-2.5 py-1.5 pr-1 pl-2',
                        active && 'bg-brand-subtle',
                        tracked ? !active && 'hover:bg-bg-subtle' : 'opacity-50',
                      )}
                    >
                      <span className="relative shrink-0">
                        <Avatar
                          size={32}
                          src={account.avatarUrl ?? undefined}
                          initials={account.initials}
                          alt=""
                        />
                        <PlatformDot
                          platform={account.platform}
                          size="sm"
                          className="absolute top-5 left-5"
                        />
                      </span>
                      <span className="text-body-sm-strong text-text-primary min-w-0 flex-1 truncate">
                        {account.handle}
                        {!tracked && ' · not tracked'}
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        </Stack>
      ))}

      <Button
        variant="outline"
        leadingIcon={Link}
        onClick={onConnectAccount}
        className="mt-auto"
      >
        Connect account
      </Button>
    </Stack>
  );
}
